// [PURPOSE]: Shared Effect Builder UI controls used by every Content Workshop
//   creator (Subclass, Class, Race, Spell). Each creator authors features that
//   need to register effects through effectRegistry; rather than duplicating
//   the same picker / row / choice-block UI four times, all four import these.
// [WHY]: BC-12 introduced the per-feature Effect Builder for subclasses. The
//   class / race / spell creators need the same picker. Extracting the
//   components here keeps the schema honest — when we change the supported
//   effect types or condition flags, all four creators upgrade in lockstep.
// [INTEGRATION]: Imported by CustomSubclassCreator, CustomClassCreator,
//   CustomRaceCreator, CustomSpellCreator. The vocab (EFFECT_TYPES,
//   OPERATIONS_BY_TYPE, etc.) lives in src/data/subclassEffectBuilderVocab.js
//   — same source of truth.

import React, { useState, useMemo } from 'react';
import { Plus, Trash2, ChevronDown, ChevronRight, Zap } from 'lucide-react';
// [SUB-PLAN 4F-preview]: Live effect summary card per authored row.
import EffectSummaryCard from '../composer/EffectSummaryCard';
import ConditionKindSlot from '../composer/ConditionKindSlot';
// [SUB-PLAN 1H-H-cond Phase 2 — TASK 2 trigfilters]: ReferencePicker + catalog
//   constants for the ammoTypeMatch / targetTypeMatch / damageTypeMatch filter
//   inputs in the triggeredEffect structured form. Replaces free-text <input>
//   where a constrained catalog exists (S1 anti-pattern elimination).
import ReferencePicker from '../composer/ReferencePicker';
import { AMMO_TYPE_OPTIONS } from '../../data/customFeatVocabulary';
import {
  EFFECT_TYPES,
  OPERATIONS_BY_TYPE,
  CONDITION_FLAGS,
  TARGET_HINT_BY_TYPE,
  // [BATCH 10d / P1 D-18 — APR 2026]: Canonical autocomplete option lists
  //   per type. Wired into a <datalist> below the target input so authors
  //   see the registry-recognized ids without leaving the field.
  TARGET_OPTIONS_BY_TYPE,
  DAMAGE_TYPE_OPTIONS,
  SENSE_OPTIONS,
  USES_FORMULAS,
  // [PHASE B / #028 — 2026-05-06]: causeCondition extra-control vocabularies.
  TRIGGER_OPTIONS,
  SAVE_TYPE_OPTIONS,
  DURATION_OPTIONS,
} from '../../data/subclassEffectBuilderVocab';
// [PHASE B / #028 — 2026-05-06]: Shared D&D 5e conditions data (#027).
//   The causeCondition value picker reads from SRD_CONDITION_OPTIONS so
//   the picker, ConditionsTracker, and any custom-condition creator
//   share one source of truth (Rule 11 / no string-domain matching).
import { SRD_CONDITION_OPTIONS } from '../../data/srdConditions';
// [SPEC C TASK 10 — 2026-05-17]: Triggered-effect vocabulary for CW author.
//   The TRIGGER_VOCABULARY enum feeds the trigger-id dropdown and gates the
//   condition-filter input fields by each trigger's allowedFilterKeys.
import {
  TRIGGER_VOCABULARY,
  getTriggerById,
  emptyTriggeredEffectShape,
} from '../../data/triggerVocabulary';

// [UF-009 — 2026-05-09]: Per-type author-facing inline help. Authored
//   locally (not derived from the vocab) so the wording avoids overlap
//   with SRD condition labels that other tests scan the DOM for. Each
//   entry should be a single sentence, plain-English, no SRD condition
//   names (Blinded, Charmed, Frightened, Grappled, Paralyzed, Poisoned,
//   Prone, Stunned, etc.). When you add a new EFFECT_TYPE in the vocab
//   file, mirror it here too.
export const EFFECT_TYPE_HELP = Object.freeze({
  criticalRange:   'Lower the natural die needed for a critical hit (e.g., crit on 19 or 20).',
  damageBonus:     'Flat or dice-based damage added to attacks (e.g., +2 or +1d8 fire).',
  attackBonus:     'Bonus added to attack rolls (to-hit).',
  damageReduction: 'Subtract a flat amount from incoming damage of a chosen type.',
  ac:              'Bonus to Armor Class, or set AC to a specific value.',
  hp:              'Bonus to maximum hit points (numbers or formulas like sorcererLevel*1).',
  speed:           'Bonus to walking speed, or set the speed outright.',
  initiative:      'Bonus added to initiative rolls.',
  spellDC:         'Bonus added to your spell save DC.',
  carryCapacity:   'Multiplier on carry / lift / push / drag capacity.',
  resistance:      'Halve damage from a chosen damage type (or grant immunity).',
  sense:           'Grant a sense such as darkvision or tremorsense at a chosen range.',
  proficiency:     'Grant proficiency with a skill, save, tool, weapon, or armor.',
  advantage:       'Grant advantage on a roll category (e.g., save:charm, attack:vowed).',
  feature:         'Set a flag the UI or combat code keys off (e.g., reckless_attack).',
  skill:           'Bonus to a single skill check (use target like skill:perception).',
  extra_attack:    'Add another attack on the Attack action (use operation: add).',
  perRestSpell:    'Grant a spell castable a fixed number of times per rest.',
  atWillSpell:     'Grant a spell castable at will, with no slot or daily limit.',
  ritualSpell:     'Grant a spell as a ritual even if it is not on your class list.',
  learnedSpell:    'Add a spell to the known list. Casting still costs a slot.',
  spellModifier:   'Modify a spell at cast time (range, damage, DC). Set target to the spell name.',
  hitDieRecovery:  'Grant extra HP when spending hit dice on a short rest.',
  causeCondition:  'Apply a status effect on a triggered save (configure DC, save ability, and duration below).',
  imposeDisadvantage: 'Impose disadvantage on a target ability\'s rolls on a failed save (configure ability, scope, DC, save ability, and duration below).',
  // [Spec A Task 10 — 2026-05-16]: Slot-recovery action description.
  slotRecoveryAction: 'Invokable Class Actions button that recovers expended spell slots from a pool. Wizard Arcane Recovery is the SRD example: ceil(classLevel/2) levels of slots, max 5th-level.',
  // [Spec B Task 14 — 2026-05-16]: Action-surface effect descriptions.
  spendActionMenu: 'Curated list of "spend N pool resource → do thing" options attached to a class resource. Surfaces in ClassResourcesPanel "Spend Options" pane (e.g., Monk Ki Spend Menu).',
  reactionTrigger: 'Invokable reaction ability surfaced in StatsPanel "Reactions" section. Click Invoke decrements the attached pool by the cost (e.g., Flash of Genius — 1 use from flash_of_genius pool).',
  longRestPicker:  'Post-long-rest picker (DailySetupModal) that binds chosen options to inventory items per long rest (e.g., Infuse Item, Spell-Storing Item).',
});

// ============================================================================
// [Spec A Task 10 — 2026-05-16]: Helpers for the pickedAtLevelUp shape on
//   atWillSpell / perRestSpell, and for the always-object slotRecoveryAction
//   value. CW authors flip "Picked at level-up" to delegate the spell choice
//   to the player at level-up time (Wizard Spell Mastery L18 / Signature
//   Spells L20 pattern).
// ============================================================================

/** True when effect.value is the pickedAtLevelUp object shape. */
export function isPickedAtLevelUp(effect) {
  return !!(effect && effect.value && typeof effect.value === 'object' && effect.value.pickedAtLevelUp);
}

/**
 * Build an empty pickedAtLevelUp value for the given type. perRestSpell adds
 * usesPerDay + rechargeOn metadata on top of the atWillSpell shape.
 */
export function emptyPickedShape(type) {
  const base = {
    pickedAtLevelUp: true,
    className: '',
    featureId: '',
    pickConstraints: { count: 1, forcedLevels: [], fromSpellbookOnly: false },
  };
  if (type === 'perRestSpell') {
    return { ...base, usesPerDay: 1, rechargeOn: 'short_rest' };
  }
  return base;
}

/** Default slotRecoveryAction value object.
 *  [Gen-4-A — 2026-05-30]: poolNounPlural + tierNoun added so a CW author
 *  declaring a slotRecoveryAction can override the "levels of spell slots"
 *  copy in SlotRecoveryModal. Empty strings = fall back to D&D defaults at
 *  SlotRecoveryModal.js:114-115 — no behavior change for existing Wizard data.
 */
export function emptySlotRecoveryActionShape() {
  return {
    actionName: '',
    budgetFormula: 'ceil(classLevel/2)',
    maxSlotTier: 5,
    spellSlotPool: 'spellSlots',
    classKey: '',
    poolResourceId: '',
    poolNounPlural: '',
    tierNoun: '',
    // [A49 AT-2 — 2026-06-03]: When true, a "Use" button appears in
    //   ClassResourcesPanel at any time (not just after a rest). The
    //   RestPanel still renders the action as a superset (rest context
    //   always includes all eligible actions). Default false keeps the
    //   existing Wizard Arcane Recovery rest-only behavior byte-identical.
    invokeAnytime: false,
  };
}

// ============================================================================
// [Spec B Task 14 — 2026-05-16]: Helpers for the 3 new action-surface value
//   shapes — spendActionMenu, reactionTrigger, longRestPicker. Each value is
//   an object the runtime consumes; the EffectRow renders a structured form
//   instead of the generic string-value input.
// ============================================================================

/** Default spendActionMenu value object (mirrors Monk Ki Spend Menu shape). */
export function emptySpendActionMenuShape() {
  return {
    menuId: '',
    attachedToResource: '',
    classKey: '',
    options: [],
  };
}

/** Default per-option entry inside a spendActionMenu.options array.
 *  [Gen-5-A — 2026-05-30]: costType defaults to 'integer' for backward
 *  compat (all existing Ki / Superiority Dice options use integer cost).
 *  Values: 'integer' (default) | 'binary' | 'none'.
 *  'none' → ClassResourcesPanel renders "Free" instead of a cost badge
 *  (Fork A resolution from 4N spec). Old saved options without costType
 *  fall back to 'integer' via the `opt.costType || 'integer'` read pattern.
 *
 *  [Gen-5-B BREADCRUMB — Daggerheart]: costType is the system-agnostic seam for
 *  resource-cost models. Daggerheart abilities spend from Hope/Fear/Stress pools,
 *  not integer Ki — the 'none'/pool-generic shape (and consumeTriggeredSource's
 *  future {poolId,amount} shape) is where a non-D&D ruleset plugs its cost
 *  semantics without touching this catalog. See master-queue Gen-6 + Sub-plan 5C.
 */
export function emptySpendActionMenuOption() {
  return { name: '', cost: 1, minLevel: 1, description: '', costType: 'integer' };
}

/** Default reactionTrigger value object (mirrors Flash of Genius shape). */
export function emptyReactionTriggerShape() {
  return {
    actionName: '',
    description: '',
    attachedToResource: '',
    cost: 1,
    classKey: '',
  };
}

/** Default longRestPicker value object (mirrors Infuse Item shape).
 *  [Gen-3-A — 2026-05-30]: nounSingular added so a CW author declaring a
 *  longRestPicker effect can author the noun shown in DailySetupModal
 *  ("Choose {nounSingular}"). Empty string = fall back to 'infusion' at
 *  DailySetupModal.js:138 — no behavior change for existing Artificer data.
 *  [Gen-3-B — 2026-05-31]: nounPlural added; consumed by DailySetupModal
 *  "Assign N {nounPlural}" heading. Empty string falls back to 'slots'.
 */
export function emptyLongRestPickerShape() {
  return {
    pickerId: '',
    knownTable: {},
    activeTable: {},
    pickSource: 'class_spell_list',
    bindTarget: 'item',
    pickKnownAtLevelUp: false,
    classKey: '',
    chargesFormula: '',
    nounSingular: '',
    nounPlural: '',
  };
}

/**
 * Parse a level-table string like "2:4, 6:6, 10:8" into a {level: count} map.
 *   - Trims whitespace; tolerates empty input.
 *   - Drops malformed pairs silently.
 *   - Returns {} for empty / whitespace-only input.
 */
export function parseLevelTableString(raw) {
  if (typeof raw !== 'string') return {};
  const result = {};
  raw.split(',').forEach(pair => {
    const [k, v] = pair.split(':').map(s => (s || '').trim());
    const lvl = parseInt(k, 10);
    const count = parseInt(v, 10);
    if (Number.isFinite(lvl) && Number.isFinite(count) && lvl >= 0 && count >= 0) {
      result[lvl] = count;
    }
  });
  return result;
}

/** Format a {level: count} map back to a "2:4, 6:6" string for display. */
export function formatLevelTableString(table) {
  if (!table || typeof table !== 'object') return '';
  return Object.keys(table)
    .map(k => parseInt(k, 10))
    .filter(n => Number.isFinite(n))
    .sort((a, b) => a - b)
    .map(lvl => `${lvl}:${table[lvl]}`)
    .join(', ');
}

/** Parse a comma-separated list of levels (string) → number[]. */
function parseForcedLevels(raw) {
  if (typeof raw !== 'string') return [];
  return raw
    .split(',')
    .map(s => parseInt(s.trim(), 10))
    .filter(n => Number.isFinite(n) && n >= 0);
}

// [Phase 4b BC-12 — APR 2026]: Stable id generator for inline effect rows so
//   React can key them properly across add/remove without losing focus.
export function makeId(prefix) {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

// [Phase 4b BC-12]: Default empty effect row (used by addEffect handlers).
// [CWR-006 FIX — MAY 2026]: Default changed from criticalRange/min (a niche
//   Champion-style feature) to damageBonus/add. Authors most commonly use
//   damageBonus, AC, and ability bonuses; criticalRange forced an extra
//   dropdown change for the typical case. Per UF-009 / Wave-2 review.
export function emptyEffect() {
  return {
    _id: makeId('eff'),
    type: 'damageBonus',
    operation: 'add',
    value: '',
    target: '',
    condition: '',
  };
}

// [Phase 4b BC-12]: Default empty choice option (used by addChoiceOption).
// [FIX MAY 2026]: Added `description` field — per user smoke-test feedback,
//   the CW choice picker had only a name field, no way to author per-option
//   descriptions. The picker (e.g. Martial Adept maneuvers) renders the
//   description under the name and as a hover title for tooltip access.
export function emptyChoiceOption() {
  return { _id: makeId('opt'), name: '', description: '', effects: [] };
}

// ============================================================================
// EffectRow — single effect editor (type / op / value / target / condition).
// ============================================================================
// [STONE 4 / #61 — 2026-05-18]: `allowedEffectTypes` is an optional array of
//   EFFECT_TYPES `value` strings. When provided AND non-empty, the type
//   dropdown filters to that set, PLUS always includes the effect's
//   currently-selected type even if not in the list (so switching the
//   parent context doesn't silently drop a pre-existing effect type).
//   When omitted or empty, all EFFECT_TYPES render — backward-compatible
//   default.
export const EffectRow = ({ effect, onChange, onRemove, allowedEffectTypes }) => {
  const allowedOps = OPERATIONS_BY_TYPE[effect.type] || ['add'];
  const targetHint = TARGET_HINT_BY_TYPE[effect.type] || '';

  // Type dropdown filter (Stone 4 / #61). Empty array → render all (defensive).
  const visibleTypes = useMemo(() => {
    if (!Array.isArray(allowedEffectTypes) || allowedEffectTypes.length === 0) {
      return EFFECT_TYPES;
    }
    const allowed = new Set(allowedEffectTypes);
    // Always include the currently selected type — never silently drop user data
    if (effect.type) allowed.add(effect.type);
    return EFFECT_TYPES.filter((t) => allowed.has(t.value));
  }, [allowedEffectTypes, effect.type]);

  // [Spec A Task 10 — 2026-05-16]: When type is slotRecoveryAction OR
  //   when type is atWillSpell/perRestSpell with pickedAtLevelUp ON,
  //   the standard string-value + target row is replaced with a
  //   structured object form (see "Structured object form" block below).
  // [Spec B Task 14 — 2026-05-16]: Same pattern for spendActionMenu,
  //   reactionTrigger, longRestPicker — each renders a structured form
  //   instead of the generic string-value input.
  const isSlotRecovery = effect.type === 'slotRecoveryAction';
  const isSpendActionMenu = effect.type === 'spendActionMenu';
  const isReactionTrigger = effect.type === 'reactionTrigger';
  const isLongRestPicker  = effect.type === 'longRestPicker';
  // [SPEC C TASK 10 — 2026-05-17]: triggeredEffect renders a structured form
  //   with: triggerId dropdown, condition filter inputs gated by the picked
  //   trigger's allowedFilterKeys, sourceLabel, onceThenConsume toggle, and
  //   a nested-payload editor (array of EffectRow children).
  const isTriggeredEffect = effect.type === 'triggeredEffect';
  const supportsPickedAtLevelUp = effect.type === 'atWillSpell' || effect.type === 'perRestSpell';
  const isPickedShape = supportsPickedAtLevelUp && isPickedAtLevelUp(effect);
  const useStructuredForm = isSlotRecovery || isPickedShape || isSpendActionMenu || isReactionTrigger || isLongRestPicker || isTriggeredEffect;

  let valueField;
  if (effect.type === 'resistance') {
    valueField = (
      <select
        value={effect.value || ''}
        onChange={(e) => onChange('value', e.target.value)}
        className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
      >
        <option value="">-- damage type --</option>
        {DAMAGE_TYPE_OPTIONS.map(dt => (
          <option key={dt} value={dt}>{dt}</option>
        ))}
      </select>
    );
  } else if (effect.type === 'causeCondition') {
    // [PHASE B / #028 — 2026-05-06]: causeCondition value picks from
    //   SRD_CONDITION_OPTIONS (`src/data/srdConditions.js`). Trigger /
    //   dc / saveType / duration are authored via the extra-controls
    //   block below the standard row 2.
    valueField = (
      <select
        value={effect.value || ''}
        onChange={(e) => onChange('value', e.target.value)}
        className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
      >
        <option value="">-- condition --</option>
        {SRD_CONDITION_OPTIONS.map(opt => (
          <option key={opt.value} value={opt.value}>
            {opt.icon} {opt.label}
          </option>
        ))}
      </select>
    );
  } else if (effect.type === 'sense') {
    valueField = (
      <input
        type="text"
        list={`sense-options-${effect._id}`}
        value={effect.value || ''}
        onChange={(e) => onChange('value', e.target.value)}
        placeholder="darkvision / 60 ft"
        className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
      />
    );
  } else {
    valueField = (
      <input
        type="text"
        value={effect.value || ''}
        onChange={(e) => onChange('value', e.target.value)}
        placeholder={effect.type === 'feature'
          ? 'flag id (e.g., reckless_attack)'
          : ['perRestSpell', 'atWillSpell', 'ritualSpell'].includes(effect.type)
            ? 'Spell name'
            : effect.operation === 'add_dice'
              ? '1d8 OR rogueLevel/2:d6:roundUp'
              : 'number or formula (e.g., cha_modifier)'}
        className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
      />
    );
  }

  return (
    <div className="bg-gray-800 border border-gray-700 rounded p-2 space-y-1.5">
      {effect.type === 'sense' && (
        <datalist id={`sense-options-${effect._id}`}>
          {SENSE_OPTIONS.map(s => <option key={s} value={s} />)}
        </datalist>
      )}

      {/* Row 1: type + operation + remove */}
      <div className="flex gap-1.5 items-center">
        {/* [UF-009 — 2026-05-09]: title= on the select uses the active type's
            description (from EFFECT_TYPES vocab) so a hover tooltip works on
            desktop. Per-option title= surfaces the same description on the
            individual option (browser support varies but it's a free win).
            The visible inline help below is the primary affordance. */}
        <select
          value={effect.type}
          onChange={(e) => onChange('type', e.target.value)}
          title={(EFFECT_TYPES.find(t => t.value === effect.type) || {}).description || ''}
          className="flex-1 p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
        >
          {visibleTypes.map(t => (
            <option key={t.value} value={t.value} title={t.description || ''}>{t.label}</option>
          ))}
        </select>
        <select
          value={effect.operation}
          onChange={(e) => onChange('operation', e.target.value)}
          className="w-32 p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
        >
          {allowedOps.map(op => (
            <option key={op} value={op}>{op}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={onRemove}
          className="p-1 text-red-400 hover:text-red-300 transition-colors"
          title="Remove effect"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* [UF-009 — 2026-05-09]: Inline description for the selected effect
          type. The vocab in src/data/subclassEffectBuilderVocab.js carries
          its own `description` per entry, but several of those examples name
          SRD conditions (e.g., "Restrained") which collide with existing
          tests that scan the whole DOM for condition labels. We keep this
          local map authored to avoid that overlap, while staying terse and
          author-facing. If an EFFECT_TYPE lacks an entry here we fall back
          to the vocab's description as a graceful default. Update both this
          map and the vocab when adding a new effect type. */}
      {(() => {
        const local = EFFECT_TYPE_HELP[effect.type];
        if (local) {
          return (
            <p className="text-[11px] text-gray-400 italic" data-testid="effect-type-description">
              {local}
            </p>
          );
        }
        const meta = EFFECT_TYPES.find(t => t.value === effect.type);
        if (!meta || !meta.description) return null;
        return (
          <p className="text-[11px] text-gray-400 italic" data-testid="effect-type-description">
            {meta.description}
          </p>
        );
      })()}

      {/* [Spec A Task 10 — 2026-05-16]: Picked-at-level-up toggle for
          atWillSpell / perRestSpell. When ON, the player chooses the
          spell at level-up time (Wizard Spell Mastery L18 / Signature
          Spells L20 pattern); the value becomes a structured object
          carrying className + featureId + pickConstraints. */}
      {supportsPickedAtLevelUp && (
        <label className="flex items-center gap-2 text-xs text-gray-300">
          <input
            type="checkbox"
            checked={isPickedShape}
            onChange={(e) => {
              if (e.target.checked) onChange('value', emptyPickedShape(effect.type));
              else onChange('value', '');
            }}
          />
          <span>Picked at level-up <span className="text-gray-500">(player chooses the spell)</span></span>
        </label>
      )}

      {/* [Spec A Task 10 — 2026-05-16]: Structured object form replaces
          Row 2 when slotRecoveryAction or pickedAtLevelUp. Same `effect.value`
          shape the runtime consumes; CW save/load round-trips object values
          as-is via JSON. */}
      {useStructuredForm && isSlotRecovery && (() => {
        const v = (effect.value && typeof effect.value === 'object') ? effect.value : emptySlotRecoveryActionShape();
        const update = (key, val) => onChange('value', { ...v, [key]: val });
        return (
          <div className="grid grid-cols-2 gap-1.5 mt-1 p-2 bg-gray-900/40 rounded border border-purple-900/40">
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`sra-actionName-${effect._id}`}>Action Name</label>
              <input
                id={`sra-actionName-${effect._id}`}
                type="text"
                value={v.actionName || ''}
                onChange={(e) => update('actionName', e.target.value)}
                placeholder="Arcane Recovery"
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`sra-budgetFormula-${effect._id}`}>Budget Formula</label>
              <input
                id={`sra-budgetFormula-${effect._id}`}
                type="text"
                value={v.budgetFormula || ''}
                onChange={(e) => update('budgetFormula', e.target.value)}
                placeholder="ceil(classLevel/2)"
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`sra-maxSlotTier-${effect._id}`}>Max Slot Tier</label>
              <input
                id={`sra-maxSlotTier-${effect._id}`}
                type="number"
                min="1"
                max="9"
                value={v.maxSlotTier ?? 5}
                onChange={(e) => update('maxSlotTier', Math.max(1, parseInt(e.target.value, 10) || 1))}
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`sra-spellSlotPool-${effect._id}`}>Spell Slot Pool</label>
              <input
                id={`sra-spellSlotPool-${effect._id}`}
                type="text"
                value={v.spellSlotPool || ''}
                onChange={(e) => update('spellSlotPool', e.target.value)}
                placeholder="spellSlots"
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`sra-classKey-${effect._id}`}>Class Key</label>
              <input
                id={`sra-classKey-${effect._id}`}
                type="text"
                value={v.classKey || ''}
                onChange={(e) => update('classKey', e.target.value)}
                placeholder="Wizard or Custom:Fighter-Cleric"
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`sra-poolResourceId-${effect._id}`}>Pool Resource Id</label>
              <input
                id={`sra-poolResourceId-${effect._id}`}
                type="text"
                value={v.poolResourceId || ''}
                onChange={(e) => update('poolResourceId', e.target.value)}
                placeholder="arcane_recovery"
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>
            {/* [Gen-4-A — 2026-05-30]: Pool noun + tier noun fields.
                SlotRecoveryModal reads poolNounPlural at L114 and tierNoun at
                L115. Empty values preserve the D&D defaults ("levels of spell
                slots" / "-level" suffix). A CW author building a non-D&D
                recovery action (e.g., "arcane charges" / "circle") types their
                nouns here. */}
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`sra-poolNounPlural-${effect._id}`}>
                Pool Noun (plural) <span className="text-gray-500" title="Shown in recovery prompt. Defaults to 'levels of spell slots'.">(defaults to 'levels of spell slots')</span>
              </label>
              <input
                id={`sra-poolNounPlural-${effect._id}`}
                type="text"
                value={v.poolNounPlural || ''}
                onChange={(e) => update('poolNounPlural', e.target.value)}
                placeholder="levels of spell slots"
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`sra-tierNoun-${effect._id}`}>
                Tier Noun <span className="text-gray-500" title="Replaces '-level' in tier labels. E.g., 'circle' → '3rd circle'.">(replaces '-level' suffix — e.g., 'circle')</span>
              </label>
              <input
                id={`sra-tierNoun-${effect._id}`}
                type="text"
                value={v.tierNoun || ''}
                onChange={(e) => update('tierNoun', e.target.value)}
                placeholder="(leave blank for '-level' suffix)"
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>
            {/* [A49 AT-2 — 2026-06-03]: invokeAnytime toggle.
                When checked, a "Use <actionName>" button appears in
                ClassResourcesPanel at any time (not just during rest).
                RestPanel renders all slot-recovery actions regardless —
                rest context is a superset of invoke-anytime. */}
            <div className="col-span-2 flex items-center gap-2 mt-1">
              <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                <input
                  id={`sra-invokeAnytime-${effect._id}`}
                  type="checkbox"
                  checked={!!v.invokeAnytime}
                  onChange={(e) => update('invokeAnytime', e.target.checked)}
                />
                <span>
                  Invoke anytime{' '}
                  <span className="text-gray-500">
                    (show "Use" button in Class Resources, not just after a rest)
                  </span>
                </span>
              </label>
            </div>
          </div>
        );
      })()}

      {/* [Spec B Task 14 — 2026-05-16]: spendActionMenu structured form.
          Authors menuId / attachedToResource / classKey + an options[]
          editor (each option = name / cost / minLevel / description).
          Mirrors Monk Ki Spend Menu shape exactly.

          [Gen-5-A — 2026-05-30]: costType selector implemented (Fork A:
          'integer' | 'none'; 'binary' deferred — no SRD consumer yet).
          The BREADCRUMB below was placed here 2026-05-16 as a reminder;
          it is now resolved for 'integer' and 'none'. 'binary' and
          'dice-string' are deferred per the original breadcrumb comment
          (no SRD content uses binary; dice-string is RSE-arc). See
          COMING_SOON_ROADMAP §3.20. */}
      {useStructuredForm && isSpendActionMenu && (() => {
        const v = (effect.value && typeof effect.value === 'object') ? effect.value : emptySpendActionMenuShape();
        const opts = Array.isArray(v.options) ? v.options : [];
        const update = (key, val) => onChange('value', { ...v, [key]: val });
        const addOption = () => update('options', [...opts, emptySpendActionMenuOption()]);
        const removeOption = (idx) => update('options', opts.filter((_, i) => i !== idx));
        const updateOption = (idx, key, val) => update('options', opts.map((o, i) => (i === idx ? { ...o, [key]: val } : o)));
        return (
          <div className="grid grid-cols-2 gap-1.5 mt-1 p-2 bg-gray-900/40 rounded border border-orange-900/40">
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`sam-menuId-${effect._id}`}>Menu Id</label>
              <input
                id={`sam-menuId-${effect._id}`}
                type="text"
                value={v.menuId || ''}
                onChange={(e) => update('menuId', e.target.value)}
                placeholder="monk_ki_spend"
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`sam-attachedToResource-${effect._id}`}>Attached To Resource</label>
              <input
                id={`sam-attachedToResource-${effect._id}`}
                type="text"
                value={v.attachedToResource || ''}
                onChange={(e) => update('attachedToResource', e.target.value)}
                placeholder="ki_points"
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>
            <div className="col-span-2">
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`sam-classKey-${effect._id}`}>Class Key</label>
              <input
                id={`sam-classKey-${effect._id}`}
                type="text"
                value={v.classKey || ''}
                onChange={(e) => update('classKey', e.target.value)}
                placeholder="Monk or Custom:Fighter-Cleric"
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>
            <div className="col-span-2 space-y-1.5">
              <p className="text-[10px] text-gray-400">Options ({opts.length})</p>
              {opts.map((opt, i) => {
                /* [Gen-5-A — 2026-05-30]: costType selector (Fork A: integer/none).
                   Old options without costType default to 'integer'. */
                const costType = opt.costType || 'integer';
                return (
                <div key={`sam-opt-${effect._id}-${i}`} className="grid grid-cols-12 gap-1 items-center p-1 bg-gray-800/60 rounded">
                  <input
                    type="text"
                    value={opt.name || ''}
                    onChange={(e) => updateOption(i, 'name', e.target.value)}
                    placeholder="Option name (e.g., Flurry of Blows)"
                    className="col-span-5 p-1 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
                  />
                  {/* costType selector — inline before the cost number input */}
                  <select
                    value={costType}
                    onChange={(e) => updateOption(i, 'costType', e.target.value)}
                    title="Cost type: integer (resource units), none (free ability)"
                    className="col-span-2 p-1 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
                    aria-label="Cost type"
                  >
                    <option value="integer">integer</option>
                    <option value="none">none (free)</option>
                  </select>
                  {/* Cost number — hidden when costType is 'none' */}
                  {costType !== 'none' ? (
                    <input
                      type="number"
                      min="1"
                      value={opt.cost ?? 1}
                      onChange={(e) => updateOption(i, 'cost', Math.max(1, parseInt(e.target.value, 10) || 1))}
                      title="Cost (resource units)"
                      className="col-span-2 p-1 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
                    />
                  ) : (
                    <span className="col-span-2 p-1 text-[10px] text-emerald-400 font-semibold">Free</span>
                  )}
                  <input
                    type="number"
                    min="1"
                    value={opt.minLevel ?? 1}
                    onChange={(e) => updateOption(i, 'minLevel', Math.max(1, parseInt(e.target.value, 10) || 1))}
                    title="Min level"
                    className="col-span-1 p-1 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => removeOption(i)}
                    className="col-span-1 p-1 text-red-400 hover:text-red-300 transition-colors"
                    title="Remove option"
                  >
                    <Trash2 size={12} />
                  </button>
                  <input
                    type="text"
                    value={opt.description || ''}
                    onChange={(e) => updateOption(i, 'description', e.target.value)}
                    placeholder="Description shown in Spend Options pane"
                    className="col-span-12 p-1 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
                  />
                </div>
                );
              })}
              <button
                type="button"
                onClick={addOption}
                className="flex items-center gap-1 px-2 py-1 bg-orange-800/50 hover:bg-orange-700/60 text-orange-200 rounded text-xs transition-colors"
              >
                <Plus size={11} />
                Add Option
              </button>
            </div>
          </div>
        );
      })()}

      {/* [Spec B Task 14 — 2026-05-16]: reactionTrigger structured form.
          Mirrors Flash of Genius shape: actionName / description /
          attachedToResource / cost / classKey. */}
      {useStructuredForm && isReactionTrigger && (() => {
        const v = (effect.value && typeof effect.value === 'object') ? effect.value : emptyReactionTriggerShape();
        const update = (key, val) => onChange('value', { ...v, [key]: val });
        return (
          <div className="grid grid-cols-2 gap-1.5 mt-1 p-2 bg-gray-900/40 rounded border border-blue-900/40">
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`rt-actionName-${effect._id}`}>Action Name</label>
              <input
                id={`rt-actionName-${effect._id}`}
                type="text"
                value={v.actionName || ''}
                onChange={(e) => update('actionName', e.target.value)}
                placeholder="Flash of Genius"
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`rt-attachedToResource-${effect._id}`}>Attached To Resource</label>
              <input
                id={`rt-attachedToResource-${effect._id}`}
                type="text"
                value={v.attachedToResource || ''}
                onChange={(e) => update('attachedToResource', e.target.value)}
                placeholder="flash_of_genius"
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>
            <div className="col-span-2">
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`rt-description-${effect._id}`}>Description</label>
              <textarea
                id={`rt-description-${effect._id}`}
                value={v.description || ''}
                onChange={(e) => update('description', e.target.value)}
                placeholder="Reaction: when..., add your INT mod to the roll"
                rows={2}
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none resize-y"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`rt-cost-${effect._id}`}>Cost</label>
              <input
                id={`rt-cost-${effect._id}`}
                type="number"
                min="1"
                value={v.cost ?? 1}
                onChange={(e) => update('cost', Math.max(1, parseInt(e.target.value, 10) || 1))}
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`rt-classKey-${effect._id}`}>Class Key</label>
              <input
                id={`rt-classKey-${effect._id}`}
                type="text"
                value={v.classKey || ''}
                onChange={(e) => update('classKey', e.target.value)}
                placeholder="Artificer or Custom:Fighter-Cleric"
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>
          </div>
        );
      })()}

      {/* [Spec B Task 14 — 2026-05-16]: longRestPicker structured form.
          Mirrors Infuse Item / Spell-Storing Item shape: pickerId /
          knownTable / activeTable / pickSource / bindTarget /
          pickKnownAtLevelUp / classKey / chargesFormula. The two
          tables are authored as "level:count" comma-separated strings
          and round-tripped through parseLevelTableString /
          formatLevelTableString. */}
      {useStructuredForm && isLongRestPicker && (() => {
        const v = (effect.value && typeof effect.value === 'object') ? effect.value : emptyLongRestPickerShape();
        const update = (key, val) => onChange('value', { ...v, [key]: val });
        return (
          <div className="grid grid-cols-2 gap-1.5 mt-1 p-2 bg-gray-900/40 rounded border border-cyan-900/40">
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`lrp-pickerId-${effect._id}`}>Picker Id</label>
              <input
                id={`lrp-pickerId-${effect._id}`}
                type="text"
                value={v.pickerId || ''}
                onChange={(e) => update('pickerId', e.target.value)}
                placeholder="infuse_item"
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`lrp-pickSource-${effect._id}`}>Pick Source</label>
              <select
                id={`lrp-pickSource-${effect._id}`}
                value={v.pickSource || 'class_spell_list'}
                onChange={(e) => update('pickSource', e.target.value)}
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              >
                <option value="infusion_list">infusion_list</option>
                <option value="class_spell_list">class_spell_list</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`lrp-knownTable-${effect._id}`}>Known Table <span className="text-gray-500">(level:count pairs)</span></label>
              <input
                id={`lrp-knownTable-${effect._id}`}
                type="text"
                defaultValue={formatLevelTableString(v.knownTable)}
                onChange={(e) => update('knownTable', parseLevelTableString(e.target.value))}
                placeholder="2:4, 6:6, 10:8"
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`lrp-activeTable-${effect._id}`}>Active Table <span className="text-gray-500">(level:count pairs)</span></label>
              <input
                id={`lrp-activeTable-${effect._id}`}
                type="text"
                defaultValue={formatLevelTableString(v.activeTable)}
                onChange={(e) => update('activeTable', parseLevelTableString(e.target.value))}
                placeholder="2:2, 6:3, 10:4"
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`lrp-bindTarget-${effect._id}`}>Bind Target</label>
              <input
                id={`lrp-bindTarget-${effect._id}`}
                type="text"
                value={v.bindTarget || ''}
                onChange={(e) => update('bindTarget', e.target.value)}
                placeholder="item"
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`lrp-classKey-${effect._id}`}>Class Key</label>
              <input
                id={`lrp-classKey-${effect._id}`}
                type="text"
                value={v.classKey || ''}
                onChange={(e) => update('classKey', e.target.value)}
                placeholder="Artificer or Custom:Fighter-Cleric"
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>
            <div className="col-span-2">
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`lrp-chargesFormula-${effect._id}`}>Charges Formula <span className="text-gray-500">(optional — for chargeable bindings)</span></label>
              <input
                id={`lrp-chargesFormula-${effect._id}`}
                type="text"
                value={v.chargesFormula || ''}
                onChange={(e) => update('chargesFormula', e.target.value)}
                placeholder="intMod*2"
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>
            {/* [Gen-3-A — 2026-05-30]: Singular noun field. DailySetupModal reads
                picker.nounSingular at L138: "Choose {nounSingular || 'infusion'}".
                An empty value preserves the D&D default. A CW author building a
                non-D&D picker (e.g., "Spirit Bond") types their noun here and sees
                "Choose Spirit Bond" in the post-long-rest modal. */}
            <div className="col-span-2">
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`lrp-nounSingular-${effect._id}`}>
                Singular Noun <span className="text-gray-500" title="Shown in picker label: 'Choose {noun}'. Defaults to 'infusion'.">(shown in 'Choose …' label — defaults to 'infusion')</span>
              </label>
              <input
                id={`lrp-nounSingular-${effect._id}`}
                type="text"
                value={v.nounSingular || ''}
                onChange={(e) => update('nounSingular', e.target.value)}
                placeholder="infusion"
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>
            {/* [Gen-3-B — 2026-05-31]: Plural noun field. DailySetupModal reads
                picker.nounPlural at the "Assign N {nounPlural}" heading.
                Empty string falls back to 'slots'. */}
            <div className="col-span-2">
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`lrp-nounPlural-${effect._id}`}>
                Plural noun <span className="text-gray-500" title="Shown in 'Assign N {noun}' heading. Defaults to 'slots'.">(e.g. 'runes' — defaults to 'slots')</span>
              </label>
              <input
                id={`lrp-nounPlural-${effect._id}`}
                type="text"
                value={v.nounPlural || ''}
                onChange={(e) => update('nounPlural', e.target.value)}
                placeholder="runes"
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>
            <label className="flex items-center gap-2 text-xs text-gray-300 col-span-2">
              <input
                type="checkbox"
                checked={!!v.pickKnownAtLevelUp}
                onChange={(e) => update('pickKnownAtLevelUp', e.target.checked)}
              />
              <span>Pick known at level-up <span className="text-gray-500">(player picks options as they level — Infuse Item pattern)</span></span>
            </label>
          </div>
        );
      })()}

      {/* [SPEC C TASK 10 — 2026-05-17]: triggeredEffect structured form.
          Mirrors the design doc's author surface: triggerId dropdown,
          condition filter inputs gated by allowedFilterKeys, sourceLabel,
          onceThenConsume toggle, and a nested-payload editor that
          recursively renders EffectRow for each payload entry. Authors
          can build Arrow-of-Slaying / Flame-Tongue / Vicious-Weapon
          equivalents end-to-end without writing code (Rule 12 cardinal
          test). All shape fields round-trip through effect.value as JSON. */}
      {useStructuredForm && isTriggeredEffect && (() => {
        const v = (effect.value && typeof effect.value === 'object') ? effect.value : emptyTriggeredEffectShape();
        const update = (key, val) => onChange('value', { ...v, [key]: val });
        const updateCondition = (key, val) => {
          const nextCondition = { ...(v.condition || {}) };
          if (val === '' || val == null) {
            delete nextCondition[key];
          } else {
            nextCondition[key] = val;
          }
          onChange('value', { ...v, condition: nextCondition });
        };
        const triggerMeta = getTriggerById(v.triggerId) || TRIGGER_VOCABULARY[0];
        // Filter keys other than 'when' (handled separately for consistency).
        const filterKeys = (triggerMeta.allowedFilterKeys || []).filter((k) => k !== 'when');

        // Payload row helpers — nested EffectRow children. _id is stable per
        // row so React keys survive add/remove without losing focus.
        const payload = Array.isArray(v.payload) ? v.payload : [];
        const addPayloadRow = () => {
          update('payload', [...payload, { ...emptyEffect() }]);
        };
        const updatePayloadRow = (idx, key, val) => {
          const next = payload.map((row, i) => (i === idx ? { ...row, [key]: val } : row));
          update('payload', next);
        };
        const removePayloadRow = (idx) => {
          const next = payload.filter((_, i) => i !== idx);
          update('payload', next);
        };

        return (
          <div className="grid grid-cols-2 gap-1.5 mt-1 p-2 bg-gray-900/40 rounded border border-amber-900/40">
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`te-triggerId-${effect._id}`}>Trigger Id</label>
              <select
                id={`te-triggerId-${effect._id}`}
                value={v.triggerId || 'on_hit'}
                onChange={(e) => onChange('value', { ...v, triggerId: e.target.value, condition: {} })}
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              >
                {TRIGGER_VOCABULARY.map((t) => (
                  <option key={t.value} value={t.value} title={t.description || ''}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`te-sourceLabel-${effect._id}`}>Source Label</label>
              <input
                id={`te-sourceLabel-${effect._id}`}
                type="text"
                value={v.sourceLabel || ''}
                onChange={(e) => update('sourceLabel', e.target.value)}
                placeholder="Flame Tongue"
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>

            {triggerMeta.description && (
              <p className="text-[11px] text-gray-400 italic col-span-2" data-testid="trigger-description">
                {triggerMeta.description}
              </p>
            )}

            <label className="flex items-center gap-2 text-xs text-gray-300 col-span-2">
              <input
                type="checkbox"
                checked={!!v.onceThenConsume}
                onChange={(e) => update('onceThenConsume', e.target.checked)}
              />
              <span>Once then consume <span className="text-gray-500">(decrements sourceRef after first fire — Arrow of Slaying / Mind Sharpener pattern)</span></span>
            </label>

            {/* Condition filter inputs — gated by the picked trigger's allowedFilterKeys. */}
            <div className="col-span-2 mt-1">
              <p className="text-[10px] text-gray-400 mb-1">Condition Filters <span className="text-gray-500">(leave blank to always match)</span></p>
              {/* `when` is universal — uses CONDITION_FLAGS vocab for consistency with the rest of EffectRow. */}
              <div className="grid grid-cols-2 gap-1.5">
                <div>
                  <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`te-when-${effect._id}`}>when</label>
                  <select
                    id={`te-when-${effect._id}`}
                    value={(v.condition && v.condition.when) || ''}
                    onChange={(e) => updateCondition('when', e.target.value)}
                    className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
                  >
                    {/* CONDITION_FLAGS is an array of {value, description} objects.
                        Include the empty-value "always" sentinel inline since
                        the vocab also carries one as its first entry. */}
                    {CONDITION_FLAGS.filter(f => f.value !== '').length === 0 && (
                      <option value="">-- always --</option>
                    )}
                    {CONDITION_FLAGS.map((flag) => (
                      <option key={flag.value || 'always'} value={flag.value} title={flag.description || ''}>
                        {flag.value === '' ? '-- always --' : flag.value}
                      </option>
                    ))}
                  </select>
                </div>
                {/* [SUB-PLAN 1H-H-cond Phase 2 — TASK 2 trigfilters]:
                    ammoTypeMatch / targetTypeMatch / damageTypeMatch now use
                    constrained pickers (the S1 anti-pattern = free-text where a
                    catalog exists). Other filter keys (ability, restType,
                    combatantKind, hpThreshold) remain free-text — no catalog
                    for those. The stored token contract is UNCHANGED: each key
                    still writes to v.condition[key] as a plain string, which
                    _triggeredEffectMatches reads via _readTriggerContextField. */}
                {filterKeys.map((key) => {
                  const condVal = (v.condition && v.condition[key]) || '';

                  // --- ammoTypeMatch: constrained 4-entry ammo vocab ---
                  if (key === 'ammoTypeMatch') {
                    return (
                      <div key={key}>
                        <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`te-cond-${key}-${effect._id}`}>{key}</label>
                        <select
                          id={`te-cond-${key}-${effect._id}`}
                          value={condVal}
                          onChange={(e) => updateCondition(key, e.target.value)}
                          className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
                        >
                          <option value="">-- any ammo --</option>
                          {AMMO_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.id} value={opt.id}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    );
                  }

                  // --- targetTypeMatch: constrained 14-entry creatureType kind ---
                  if (key === 'targetTypeMatch') {
                    return (
                      <div key={key}>
                        <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`te-cond-${key}-${effect._id}`}>{key}</label>
                        <ReferencePicker
                          id={`te-cond-${key}-${effect._id}`}
                          kind="creatureType"
                          value={condVal}
                          onChange={(next) => updateCondition(key, next)}
                          placeholder="-- any target type --"
                        />
                      </div>
                    );
                  }

                  // --- damageTypeMatch: constrained 13-entry damageType kind ---
                  if (key === 'damageTypeMatch') {
                    return (
                      <div key={key}>
                        <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`te-cond-${key}-${effect._id}`}>{key}</label>
                        <ReferencePicker
                          id={`te-cond-${key}-${effect._id}`}
                          kind="damageType"
                          value={condVal}
                          onChange={(next) => updateCondition(key, next)}
                          placeholder="-- any damage type --"
                        />
                      </div>
                    );
                  }

                  // --- all other filter keys: free-text <input> (no catalog exists) ---
                  return (
                    <div key={key}>
                      <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`te-cond-${key}-${effect._id}`}>{key}</label>
                      <input
                        id={`te-cond-${key}-${effect._id}`}
                        type="text"
                        value={condVal}
                        onChange={(e) => updateCondition(key, e.target.value)}
                        placeholder={
                          key === 'ability' ? 'constitution' :
                          key === 'restType' ? 'long / short' :
                          key === 'combatantKind' ? 'pc / npc' :
                          key === 'hpThreshold' ? 'half / quarter' :
                          ''
                        }
                        className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Nested payload editor. Each row is a full EffectRow component
                (recursive reuse — payload entries are effect-rows themselves,
                so the same shape + same UI works inside the trigger). */}
            <div className="col-span-2 mt-1">
              <div className="flex items-center justify-between mb-1">
                <p className="text-[10px] text-gray-400">Payload <span className="text-gray-500">(effects fired on trigger match)</span></p>
                <button
                  type="button"
                  onClick={addPayloadRow}
                  className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
                  title="Add payload row"
                >
                  <Plus size={12} /> Add Payload Row
                </button>
              </div>
              {payload.length === 0 ? (
                <p className="text-[11px] text-gray-500 italic p-2 bg-gray-900/60 rounded border border-gray-700">
                  No payload effects yet. Click "Add Payload Row" to add what fires when this trigger matches.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {payload.map((row, idx) => (
                    <EffectRow
                      key={row._id || idx}
                      effect={row}
                      onChange={(key, val) => updatePayloadRow(idx, key, val)}
                      onRemove={() => removePayloadRow(idx)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {useStructuredForm && isPickedShape && (() => {
        const v = (effect.value && typeof effect.value === 'object') ? effect.value : emptyPickedShape(effect.type);
        const pc = v.pickConstraints || { count: 1, forcedLevels: [], fromSpellbookOnly: false };
        const update = (key, val) => onChange('value', { ...v, [key]: val });
        const updateConstraint = (key, val) => onChange('value', { ...v, pickConstraints: { ...pc, [key]: val } });
        return (
          <div className="grid grid-cols-2 gap-1.5 mt-1 p-2 bg-gray-900/40 rounded border border-purple-900/40">
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`pickup-className-${effect._id}`}>Class Name</label>
              <input
                id={`pickup-className-${effect._id}`}
                type="text"
                value={v.className || ''}
                onChange={(e) => update('className', e.target.value)}
                placeholder="Custom:Fighter-Cleric"
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`pickup-featureId-${effect._id}`}>Feature Id</label>
              <input
                id={`pickup-featureId-${effect._id}`}
                type="text"
                value={v.featureId || ''}
                onChange={(e) => update('featureId', e.target.value)}
                placeholder="blade_storm_mastery"
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`pickup-count-${effect._id}`}>Count</label>
              <input
                id={`pickup-count-${effect._id}`}
                type="number"
                min="1"
                value={pc.count ?? 1}
                onChange={(e) => updateConstraint('count', Math.max(1, parseInt(e.target.value, 10) || 1))}
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`pickup-forcedLevels-${effect._id}`}>Forced Levels (comma-separated)</label>
              <input
                id={`pickup-forcedLevels-${effect._id}`}
                type="text"
                value={Array.isArray(pc.forcedLevels) ? pc.forcedLevels.join(', ') : ''}
                onChange={(e) => updateConstraint('forcedLevels', parseForcedLevels(e.target.value))}
                placeholder="1, 2"
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              />
            </div>
            <label className="flex items-center gap-1 text-xs text-gray-300 col-span-2">
              <input
                type="checkbox"
                checked={!!pc.fromSpellbookOnly}
                onChange={(e) => updateConstraint('fromSpellbookOnly', e.target.checked)}
              />
              <span>Spellbook only <span className="text-gray-500">(restrict picks to spells already in the spellbook)</span></span>
            </label>
            {effect.type === 'perRestSpell' && (
              <>
                <div>
                  <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`pickup-usesPerDay-${effect._id}`}>Uses Per Day</label>
                  <input
                    id={`pickup-usesPerDay-${effect._id}`}
                    type="number"
                    min="1"
                    value={v.usesPerDay ?? 1}
                    onChange={(e) => update('usesPerDay', Math.max(1, parseInt(e.target.value, 10) || 1))}
                    className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-gray-400 block mb-0.5" htmlFor={`pickup-rechargeOn-${effect._id}`}>Recharge On</label>
                  <select
                    id={`pickup-rechargeOn-${effect._id}`}
                    value={v.rechargeOn || 'short_rest'}
                    onChange={(e) => update('rechargeOn', e.target.value)}
                    className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
                  >
                    <option value="short_rest">Short rest</option>
                    <option value="long_rest">Long rest</option>
                  </select>
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* Row 2: value + target — hidden when the structured object form
          replaces it (slotRecoveryAction OR pickedAtLevelUp). */}
      {!useStructuredForm && (
      <div className="grid grid-cols-2 gap-1.5">
        <div>
          <label className="text-[10px] text-gray-400 block mb-0.5">Value</label>
          {valueField}
        </div>
        <div>
          <label className="text-[10px] text-gray-400 block mb-0.5">
            Target {targetHint && <span className="text-gray-500">({targetHint})</span>}
          </label>
          {/* [BATCH 10d / P1 D-18 — APR 2026]: Datalist autocomplete from
              TARGET_OPTIONS_BY_TYPE. Free text still accepted (registry
              consumes the raw string), but the suggestion list keeps
              spelling honest. */}
          {(TARGET_OPTIONS_BY_TYPE[effect.type] || []).length > 0 && (
            <datalist id={`target-options-${effect._id}`}>
              {TARGET_OPTIONS_BY_TYPE[effect.type].map(opt => (
                <option key={opt} value={opt} />
              ))}
            </datalist>
          )}
          <input
            type="text"
            list={
              (TARGET_OPTIONS_BY_TYPE[effect.type] || []).length > 0
                ? `target-options-${effect._id}`
                : undefined
            }
            value={effect.target || ''}
            onChange={(e) => onChange('target', e.target.value)}
            placeholder={targetHint || 'optional'}
            className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
          />
        </div>
      </div>
      )}

      {/* [PHASE B / #028 — 2026-05-06]: causeCondition extra controls.
          Authors the four fields (trigger / dc / saveType / duration)
          that the standard type-op-value-target shape can't carry on
          its own. Hidden until the type dropdown is set to
          causeCondition so the existing effect types stay visually
          unchanged.
          [2M-ability-disadvantage — 2026-05-31]: imposeDisadvantage shares the
          DC / saveType / duration sub-fields (same save-rider pattern). The
          Trigger select is causeCondition-only. imposeDisadvantage gets two
          additional selects: Ability (which ability is hampered) and Scope
          (attacks / checks / saves / all). */}
      {(effect.type === 'causeCondition' || effect.type === 'imposeDisadvantage') && (
        <div className="grid grid-cols-2 gap-1.5 mt-1 p-2 bg-gray-900/40 rounded border border-emerald-900/40">
          {effect.type === 'causeCondition' && (
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5">Trigger</label>
              <select
                aria-label="Condition trigger"
                value={effect.trigger || ''}
                onChange={(e) => onChange('trigger', e.target.value)}
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              >
                <option value="">-- when --</option>
                {TRIGGER_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          )}
          {effect.type === 'imposeDisadvantage' && (
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5">Ability</label>
              <select
                aria-label="Disadvantage ability"
                value={effect.ability || ''}
                onChange={(e) => onChange('ability', e.target.value)}
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              >
                <option value="">-- ability --</option>
                {['STR', 'DEX', 'CON', 'INT', 'WIS', 'CHA'].map(ab => (
                  <option key={ab} value={ab}>{ab}</option>
                ))}
              </select>
            </div>
          )}
          {effect.type === 'imposeDisadvantage' && (
            <div>
              <label className="text-[10px] text-gray-400 block mb-0.5">Scope</label>
              <select
                aria-label="Disadvantage scope"
                value={effect.scope || ''}
                onChange={(e) => onChange('scope', e.target.value)}
                className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
              >
                <option value="">-- scope --</option>
                <option value="attacks">attacks</option>
                <option value="checks">checks</option>
                <option value="saves">saves</option>
                <option value="all">all (attacks, checks & saves)</option>
              </select>
            </div>
          )}
          <div>
            <label className="text-[10px] text-gray-400 block mb-0.5">DC</label>
            <input
              type="text"
              aria-label="Condition save DC"
              value={effect.dc !== undefined && effect.dc !== null ? String(effect.dc) : ''}
              onChange={(e) => {
                const raw = e.target.value;
                // Accept numeric (e.g., 15) or formula (e.g., spellSaveDC).
                // Store the raw string and let the registry resolve at apply time.
                const num = parseInt(raw, 10);
                if (raw === '') onChange('dc', '');
                else if (!isNaN(num) && String(num) === raw) onChange('dc', num);
                else onChange('dc', raw);
              }}
              placeholder="15 or spellSaveDC"
              className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
            />
          </div>
          <div>
            <label className="text-[10px] text-gray-400 block mb-0.5">Save Ability</label>
            <select
              aria-label="Condition save ability"
              value={effect.saveType || ''}
              onChange={(e) => onChange('saveType', e.target.value)}
              className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
            >
              <option value="">-- ability --</option>
              {SAVE_TYPE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-gray-400 block mb-0.5">Duration</label>
            <select
              aria-label="Condition duration"
              value={effect.duration || ''}
              onChange={(e) => onChange('duration', e.target.value)}
              className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
            >
              <option value="">-- duration --</option>
              {DURATION_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* perRestSpell extra controls: recharge + uses */}
      {effect.type === 'perRestSpell' && (
        <div className="flex flex-wrap gap-2 mt-1">
          {/* Recharge */}
          <select
            className="rounded border border-gray-600 bg-gray-700 text-white text-xs px-2 py-1"
            value={effect.recharge || 'long_rest'}
            onChange={e => onChange('recharge', e.target.value)}
          >
            <option value="long_rest">Long rest</option>
            <option value="short_rest">Short rest</option>
          </select>

          {/* Uses mode toggle */}
          <label className="flex items-center gap-1 text-xs text-gray-300">
            <input
              type="radio"
              name={`uses-mode-${effect._id}`}
              checked={typeof effect.uses !== 'string' || effect.uses === ''}
              onChange={() => onChange('uses', 1)}
            />
            Fixed
          </label>
          <label className="flex items-center gap-1 text-xs text-gray-300">
            <input
              type="radio"
              name={`uses-mode-${effect._id}`}
              checked={typeof effect.uses === 'string' && effect.uses !== ''}
              onChange={() => onChange('uses', USES_FORMULAS[0].value)}
            />
            Formula
          </label>

          {/* Fixed: number input */}
          {(typeof effect.uses !== 'string' || effect.uses === '') && (
            <input
              type="number"
              min="1"
              className="w-16 rounded border border-gray-600 bg-gray-700 text-white text-xs px-2 py-1"
              value={typeof effect.uses === 'number' ? effect.uses : 1}
              onChange={e => onChange('uses', Math.max(1, parseInt(e.target.value, 10) || 1))}
            />
          )}

          {/* Formula: ability dropdown */}
          {typeof effect.uses === 'string' && effect.uses !== '' && (
            <select
              className="rounded border border-gray-600 bg-gray-700 text-white text-xs px-2 py-1"
              value={effect.uses}
              onChange={e => onChange('uses', e.target.value)}
            >
              {USES_FORMULAS.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Row 3: condition — ConditionKindSlot (Sub-plan 1H-H-cond) */}
      <div>
        <label className="text-[10px] text-gray-400 block mb-0.5">Condition</label>
        <ConditionKindSlot
          condition={effect.condition}
          onChange={(leaf) => onChange('condition', leaf)}
          disabled={false}
        />
      </div>
    </div>
  );
};

// ============================================================================
// ChoiceOptionEditor — one option inside a choice block.
// ============================================================================
export const ChoiceOptionEditor = ({
  option,
  onUpdateName,
  onUpdateDescription, // [FIX MAY 2026]: Optional — wires per-option description input.
  onRemove,
  onAddEffect,
  onUpdateEffect,
  onRemoveEffect,
  optionLabel = 'Option',
}) => {
  return (
    <div className="bg-gray-900/60 border border-purple-900/50 rounded p-2 space-y-2">
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={option.name}
          onChange={(e) => onUpdateName(e.target.value)}
          placeholder={`${optionLabel} name (e.g., Bear, Eagle, Wolf)`}
          className="flex-1 p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
        />
        <button
          type="button"
          onClick={onRemove}
          className="p-1 text-red-400 hover:text-red-300 transition-colors"
          title={`Remove ${optionLabel.toLowerCase()}`}
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* [FIX MAY 2026]: Per-option description textarea. Renders only when
          a parent supplies onUpdateDescription so it stays opt-in for
          parents that haven't wired the new prop yet (backward compat). */}
      {onUpdateDescription && (
        <textarea
          value={option.description || ''}
          onChange={(e) => onUpdateDescription(e.target.value)}
          placeholder={`Description (shown to player in the picker — e.g. "Trip the target on a hit")`}
          rows={2}
          className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none resize-y"
        />
      )}

      <div className="space-y-1.5 ml-2">
        {(option.effects || []).map((eff, j) => (
          <EffectRow
            key={eff._id || `opt-eff-${j}`}
            effect={eff}
            onChange={(field, value) => onUpdateEffect(j, field, value)}
            onRemove={() => onRemoveEffect(j)}
          />
        ))}
        <button
          type="button"
          onClick={onAddEffect}
          className="flex items-center gap-1 px-2 py-1 bg-purple-800/50 hover:bg-purple-700/60 text-purple-200 rounded text-xs transition-colors"
        >
          <Plus size={11} />
          Add Effect to "{option.name || optionLabel.toLowerCase()}"
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// FeatureEffectsSection — the collapsible "Mechanical Effects" subsection
// (always-on effects + optional choice block) attached to a single feature.
// All callbacks are bound by the parent — this component is purely
// presentational, so it can sit inside any feature shape (subclass-by-level,
// class-by-level, race-flat, spell-instance, etc.) without coupling to the
// surrounding feature schema.
// ============================================================================
export const FeatureEffectsSection = ({
  effects,
  hasChoices,
  choiceKey,
  choiceOptions,
  // Always-on effect callbacks
  onAddEffect,
  onRemoveEffect,
  onUpdateEffect,
  // Choice block callbacks
  onToggleHasChoices,
  onUpdateChoiceKey,
  onAddChoiceOption,
  onRemoveChoiceOption,
  onUpdateChoiceOptionName,
  onAddChoiceOptionEffect,
  onRemoveChoiceOptionEffect,
  onUpdateChoiceOptionEffect,
  // UI labels
  // [4F-polish — 2026-05-30]: Replaced camelCase storage-path hint with plain-English
  //   wording so CW authors aren't confronted with developer-facing jargon.
  //   Callers may still pass their own hint; this default covers the generic case.
  choiceKeyHint = 'e.g., fightingStyle, pactBoon (internal registry key)',
  choiceCheckboxLabel = 'This feature offers a choice (e.g., Totem Spirit, Fighting Style)',
  // [SUB-PLAN 4F GAP-4 — 2026-05-29]: Player-facing prompt label authored by
  //   the class/feat creator. Shown as a text input when hasChoices is true so
  //   the author can write "Choose a Fighting Style:" instead of a generic default.
  //   Persisted on the feature/choice shape; consumers display it as the choice header.
  choicePromptLabel = '',
  onUpdateChoicePromptLabel = null,
}) => {
  const [open, setOpen] = useState(true);

  const effectCount = (effects || []).length;
  const choiceCount = hasChoices ? (choiceOptions || []).length : 0;

  return (
    <div className="border-t border-gray-700 pt-2">
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        className="flex items-center gap-1 text-xs text-emerald-300 hover:text-emerald-200 transition-colors"
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <Zap size={12} />
        Mechanical Effects
        {(effectCount > 0 || choiceCount > 0) && (
          <span className="text-gray-500">
            ({effectCount} effect{effectCount === 1 ? '' : 's'}{choiceCount > 0 ? ` + ${choiceCount} choice${choiceCount === 1 ? '' : 's'}` : ''})
          </span>
        )}
      </button>

      {open && (
        <div className="mt-2 space-y-2">
          {/* Always-on effects */}
          <div className="space-y-1.5">
            {(effects || []).map((eff, j) => (
              <React.Fragment key={eff._id || `eff-${j}`}>
                <EffectRow
                  effect={eff}
                  onChange={(field, value) => onUpdateEffect(j, field, value)}
                  onRemove={() => onRemoveEffect(j)}
                />
                {/* [SUB-PLAN 4F-preview]: Live readable summary of the authored effect. */}
                <EffectSummaryCard effect={eff} />
              </React.Fragment>
            ))}
            <button
              type="button"
              onClick={onAddEffect}
              className="flex items-center gap-1 px-2 py-1 bg-emerald-800/50 hover:bg-emerald-700/60 text-emerald-200 rounded text-xs transition-colors"
            >
              <Plus size={11} />
              Add Effect
            </button>
          </div>

          {/* Choice block — [SUB-PLAN 4F-6]: wrapped in a named "Choice Mechanic" fieldset so authors see it as a distinct top-level section */}
          {onToggleHasChoices && (
            <fieldset
              data-testid="choice-mechanic-section"
              className="border border-purple-900/40 rounded p-2 space-y-2"
            >
              <legend className="px-1 text-xs font-semibold text-purple-300 tracking-wide uppercase">
                Choice Mechanic
              </legend>
              <div className="space-y-2">
              <label className="flex items-center gap-2 text-xs text-purple-300">
                <input
                  type="checkbox"
                  checked={!!hasChoices}
                  onChange={onToggleHasChoices}
                  className="rounded"
                />
                {choiceCheckboxLabel}
              </label>

              {hasChoices && (
                <div className="space-y-2 ml-1">
                  {onUpdateChoicePromptLabel && (
                    <div>
                      <label className="text-[10px] text-gray-400 block mb-0.5">
                        Player-facing prompt (e.g. "Choose a Fighting Style:")
                      </label>
                      <input
                        type="text"
                        value={choicePromptLabel || ''}
                        onChange={(e) => onUpdateChoicePromptLabel(e.target.value)}
                        placeholder="Choose a …"
                        className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-purple-500 outline-none"
                      />
                    </div>
                  )}
                  <div>
                    <label className="text-[10px] text-gray-400 block mb-0.5">
                      {/* [4F-polish — 2026-05-30]: Plain-English label replaces storage-path wording */}
                      Choice key (internal registry key)
                    </label>
                    <input
                      type="text"
                      value={choiceKey || ''}
                      onChange={(e) => onUpdateChoiceKey(e.target.value)}
                      placeholder={choiceKeyHint}
                      className="w-full p-1.5 bg-gray-700 text-white rounded text-xs border border-gray-600 focus:border-emerald-500 outline-none"
                    />
                  </div>

                  <div className="space-y-2">
                    {(choiceOptions || []).map((opt, j) => (
                      <ChoiceOptionEditor
                        key={opt._id || `opt-${j}`}
                        option={opt}
                        onUpdateName={(name) => onUpdateChoiceOptionName(j, name)}
                        onRemove={() => onRemoveChoiceOption(j)}
                        onAddEffect={() => onAddChoiceOptionEffect(j)}
                        onUpdateEffect={(effIdx, field, value) =>
                          onUpdateChoiceOptionEffect(j, effIdx, field, value)
                        }
                        onRemoveEffect={(effIdx) => onRemoveChoiceOptionEffect(j, effIdx)}
                      />
                    ))}
                    <button
                      type="button"
                      onClick={onAddChoiceOption}
                      className="flex items-center gap-1 px-2 py-1 bg-purple-800/50 hover:bg-purple-700/60 text-purple-200 rounded text-xs transition-colors"
                    >
                      <Plus size={11} />
                      Add Option
                    </button>
                  </div>
                </div>
              )}
              </div>
            </fieldset>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Reducer-style state-update helpers (work on a feature object that has
// {effects, hasChoices, choiceKey, choiceOptions} — the standard shape).
// Each creator wires these into its own setState path.
// ============================================================================

export function applyAddEffect(feature) {
  return { ...feature, effects: [...(feature.effects || []), emptyEffect()] };
}

export function applyRemoveEffect(feature, effectIdx) {
  return { ...feature, effects: (feature.effects || []).filter((_, j) => j !== effectIdx) };
}

export function applyUpdateEffect(feature, effectIdx, field, value) {
  const nextEffects = (feature.effects || []).map((eff, j) => {
    if (j !== effectIdx) return eff;
    const next = { ...eff, [field]: value };
    if (field === 'type') {
      const allowedOps = OPERATIONS_BY_TYPE[value] || ['add'];
      if (!allowedOps.includes(next.operation)) {
        next.operation = allowedOps[0];
      }
    }
    return next;
  });
  return { ...feature, effects: nextEffects };
}

export function applyToggleHasChoices(feature) {
  const willHave = !feature.hasChoices;
  return {
    ...feature,
    hasChoices: willHave,
    choiceOptions: willHave && (!feature.choiceOptions || feature.choiceOptions.length === 0)
      ? [emptyChoiceOption()]
      : (feature.choiceOptions || []),
  };
}

export function applyUpdateChoiceKey(feature, value) {
  return { ...feature, choiceKey: value };
}

export function applyAddChoiceOption(feature) {
  return { ...feature, choiceOptions: [...(feature.choiceOptions || []), emptyChoiceOption()] };
}

export function applyRemoveChoiceOption(feature, optIdx) {
  return { ...feature, choiceOptions: (feature.choiceOptions || []).filter((_, j) => j !== optIdx) };
}

export function applyUpdateChoiceOptionName(feature, optIdx, name) {
  return {
    ...feature,
    choiceOptions: (feature.choiceOptions || []).map((opt, j) =>
      j === optIdx ? { ...opt, name } : opt
    ),
  };
}

export function applyAddChoiceOptionEffect(feature, optIdx) {
  return {
    ...feature,
    choiceOptions: (feature.choiceOptions || []).map((opt, j) =>
      j === optIdx ? { ...opt, effects: [...(opt.effects || []), emptyEffect()] } : opt
    ),
  };
}

export function applyRemoveChoiceOptionEffect(feature, optIdx, effectIdx) {
  return {
    ...feature,
    choiceOptions: (feature.choiceOptions || []).map((opt, j) => {
      if (j !== optIdx) return opt;
      return { ...opt, effects: (opt.effects || []).filter((_, k) => k !== effectIdx) };
    }),
  };
}

export function applyUpdateChoiceOptionEffect(feature, optIdx, effectIdx, field, value) {
  return {
    ...feature,
    choiceOptions: (feature.choiceOptions || []).map((opt, j) => {
      if (j !== optIdx) return opt;
      const nextEffects = (opt.effects || []).map((eff, k) => {
        if (k !== effectIdx) return eff;
        const next = { ...eff, [field]: value };
        if (field === 'type') {
          const allowedOps = OPERATIONS_BY_TYPE[value] || ['add'];
          if (!allowedOps.includes(next.operation)) next.operation = allowedOps[0];
        }
        return next;
      });
      return { ...opt, effects: nextEffects };
    }),
  };
}

// ============================================================================
// Hydration / serialization helpers
// ============================================================================

/**
 * Hydrate a registry-shape feature-effects block into the UI state shape used
 * by the Effect Builder. Inverse of buildFeatureEffectsBlock.
 *
 * Input  shape (registry):
 *   { minLevel, effects?: [{type, operation, value, target, condition?}],
 *     choiceKey?, choices?: { [name]: { effects: [...] } }, notes? }
 *
 * Output shape (UI):
 *   { effects: [{_id, type, operation, value, target, condition}],
 *     hasChoices: boolean, choiceKey, choiceOptions: [{_id, name, effects}] }
 */
export function hydrateFeatureEffectsBlock(block) {
  if (!block) {
    return { effects: [], hasChoices: false, choiceKey: '', choiceOptions: [] };
  }
  const effects = Array.isArray(block.effects)
    ? block.effects.map(hydrateEffect)
    : [];

  const choiceOptions = block.choices
    ? Object.entries(block.choices).map(([optName, optData]) => ({
        _id: makeId('opt'),
        name: optName,
        effects: Array.isArray(optData?.effects) ? optData.effects.map(hydrateEffect) : [],
      }))
    : [];

  return {
    effects,
    hasChoices: choiceOptions.length > 0,
    choiceKey: block.choiceKey || '',
    choiceOptions,
    // [SUB-PLAN 4F GAP-4 — 2026-05-29]: Restore player-facing prompt label.
    choicePromptLabel: block.choicePromptLabel || '',
  };
}

function hydrateEffect(e) {
  return {
    _id: makeId('eff'),
    type: e.type || 'criticalRange',
    operation: e.operation || 'min',
    value: e.value === undefined ? '' : String(e.value),
    target: e.target || '',
    condition: (e.condition && e.condition.when) || '',
  };
}
